import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, ArrowRight } from "lucide-react";

import { useSelector } from "react-redux";
import {
  selectAuthError,
  selectAuthStatus,
  selectIsAuthenticated,
} from "../../store/slices/authSlice";
import { useLoginMutation } from "../../store/api/authApi";
import { validateEmail, validatePassword } from "../../utils/validation";
import { ROUTES } from "../../constants/routes";
import { signInModuleReturn } from "../../utils/signInModuleReturn";
import "./SignInLanding.css";

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const moduleReturn = signInModuleReturn(location.search);
  const status = useSelector(selectAuthStatus);
  const authError = useSelector(selectAuthError);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [login] = useLoginMutation();
  const isLoading = status === "loading";

  // Normal sign-in lands on home. Never
  // honour `location.state.from`. The "redirect to where you came
  // from" pattern is a known cross-account leak vector: when Account
  // A logs out from `/task/{A-taskId}`, PrivateRoute can capture
  // that URL into `state.from`; after Account B logs in, the redirect
  // sends them to A's task URL. The cache resets in `logout()` clear
  // A's *data*, but the URL itself survived until this change.
  //
  // Only the explicit, account-independent module entry point above is
  // allowed to return after login. That service rechecks organisation access.
  // Cold-load task deep links (e.g. clicking a shared
  // `/task/{id}` URL while signed out) no longer auto-resume after
  // login. Users land on home and navigate to their own task via the
  // sidebar — a small UX cost for closing the leak.
  const redirectPath = ROUTES.HOME;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  // Brand-statement word cycler for the left panel. The active word
  // is rendered in solid white; the rest dim to ~30% white. We
  // advance the index on a 2.2s interval so the rotation reads as
  // intentional storytelling, not jittery decoration.
  const BRAND_WORDS = ["Capture.", "Retain.", "Compound.", "Operationalize."];
  const [activeWordIndex, setActiveWordIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setActiveWordIndex((i) => (i + 1) % BRAND_WORDS.length);
    }, 2200);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Left-panel hero: image only for now. The S3 video is temporarily
  // disabled — uncomment LOGIN_VIDEO_URL + videoReady below AND the
  // <video> block in the JSX (plus restore data-hidden={videoReady} on
  // the <img>) to re-enable the crossfade.
  const LOGIN_VIDEO_URL =
    "https://harmoniq-public.s3.eu-west-2.amazonaws.com/login_video.mp4";
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("rememberMe") === "true";
    if (saved) {
      setEmail(localStorage.getItem("savedEmail") || "");

      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    // This separately hosted module requires a document navigation through
    // the app gateway; a React Router navigation would render the app's 404.
    if (moduleReturn) window.location.replace(moduleReturn);
    else navigate(redirectPath, { replace: true });
  }, [isAuthenticated, navigate, redirectPath, moduleReturn]);

  const handleField = (field, value, validator) => {
    if (field === "email") setEmail(value);
    else setPassword(value);
    setErrors((prev) => ({ ...prev, [field]: validator(value) || undefined }));
    if (submitError) setSubmitError("");
  };

  const isFormValid = !validateEmail(email) && !validatePassword(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailErr = validateEmail(email);
    const passErr = validatePassword(password);
    if (emailErr || passErr) {
      setErrors({ email: emailErr || undefined, password: passErr || undefined });
      return;
    }
    setErrors({});
    try {
      await login({ email, password }).unwrap();
      if (rememberMe) {
        localStorage.setItem("savedEmail", email);

        localStorage.setItem("rememberMe", "true");
      }
    } catch (err) {
      const data = err?.data;
      setSubmitError(
        data?.detail ||
          data?.message ||
          err?.message ||
          "Unable to sign in. Please try again.",
      );
    }
  };

  const handleRememberMe = (checked) => {
    setRememberMe(checked);
    if (!checked) {
      localStorage.removeItem("savedEmail");
      localStorage.removeItem("savedPassword");
      localStorage.removeItem("rememberMe");
    }
  };

  const displayError = submitError || authError || errors.email || errors.password;

  return (
    <div className="signin-page">
      {/* LEFT — brand panel. The hero artwork is intentionally a swappable
          asset: drop a static image at /public/auth-img/login-art.png, or
          replace the <img> with a <video src="..." autoPlay muted loop>
          when the animated version is ready. The radial gradient on
          .signin-left is the fallback so the panel never renders blank. */}
      <aside className="signin-left">
        <div
          className="signin-words"
          style={{ "--active-index": activeWordIndex }}
        >
          {/* Mark sits in its own column on the left; the wrap handles
              vertical translation, the inner <img> handles the pulse.
              Splitting the transforms across two elements keeps the
              slide and the pulse from clobbering each other (only one
              `transform` property can be set on a single element). */}
          <span className="signin-mark-wrap" aria-hidden="true">
            <img
              className="signin-mark"
              src="/hiq-logo-transaprent.svg"
              alt=""
            />
          </span>
          <div className="signin-words-list">
            {BRAND_WORDS.map((word, i) => (
              <h2
                key={word}
                className={`signin-word${i === activeWordIndex ? " signin-word--active" : ""}`}
              >
                {word}
              </h2>
            ))}
          </div>
        </div>

        <div className="signin-art" aria-hidden="true">
          <img
            className="signin-art-media signin-art-poster"
            src="/loginleft-image.jpg"
            alt=""
          />
          {/* Video temporarily disabled — image only for now. Re-enable
              by uncommenting the videoReady state above AND this block,
              and restore data-hidden={videoReady} on the <img>. */}
          <video
            className="signin-art-media signin-art-video"
            src={LOGIN_VIDEO_URL}
            poster="/loginleft-image.png"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            data-ready={videoReady}
            onCanPlayThrough={() => setVideoReady(true)}
          />
         
        </div>

        <div className="signin-aria" aria-hidden="true">
          <img
            className="signin-aria-mark"
            src="/byAria.svg"
            alt="by aria intelligent solutions"
          />
        </div>
      </aside>

      {/* RIGHT — sign-in form */}
      <main className="signin-right">
        <div className="signin-card">
          <img className="signin-logo" src="/login-logo.svg" alt="harmoniq" />
          <h2 className="signin-title">Sign In.</h2>

          <form className="signin-form" onSubmit={handleSubmit} noValidate>
            <div className="signin-field">
              <label htmlFor="signin-email">Work Email</label>
              <input
                id="signin-email"
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                value={email}
                onChange={(e) => handleField("email", e.target.value, validateEmail)}
              />
            </div>

            <div className="signin-field">
              <label htmlFor="signin-password">Password</label>
              <div className="signin-input-wrap">
                <input
                  id="signin-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => handleField("password", e.target.value, validatePassword)}
                />
                <button
                  type="button"
                  className="signin-eye"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((p) => !p)}
                >
                  {showPassword ? <EyeOff width="18" height="18" /> : <Eye width="18" height="18" />}
                </button>
              </div>
            </div>

            <label className="signin-check">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => handleRememberMe(e.target.checked)}
              />
              <span className="signin-check-box" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              Keep me signed in
            </label>

            {displayError && (
              <div className="signin-error" role="alert">
                <AlertCircle width="14" height="14" />
                <span>{displayError}</span>
              </div>
            )}

            <button className="signin-cta" type="submit" disabled={isLoading || !isFormValid}>
              <span>{isLoading ? "Signing in…" : "Sign in to Workspace"}</span>
              {!isLoading && <ArrowRight width="16" height="18" />}
            </button>

            {/* <p className="signin-signup-line">
              Don&apos;t have an account?{" "}
              <Link to={ROUTES.SIGN_UP} className="signin-signup-link">
                Sign Up
              </Link>
            </p> */}
          </form>
        </div>
      </main>
    </div>
  );
}
